import { Tag } from '@carbon/react';
import { ListTable, StatTile, StatTiles, useFormat } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { hoApi } from '../api';
import { ReportFrame } from './reportFrame';
import { rangeQuery, unwrap, useReportRange } from './reportRange';

type Menu = HoComponents['schemas']['MenuEngineeringDto'];
type Item = HoComponents['schemas']['MenuItemDto'];

const CLASSES = ['star', 'plowhorse', 'puzzle', 'dog'] as const;
const CLASS_TAG: Record<string, 'green' | 'blue' | 'purple' | 'red' | 'gray'> = {
  star: 'green',
  plowhorse: 'blue',
  puzzle: 'purple',
  dog: 'red',
  no_cost: 'gray',
};

const n = (value: number | string | null | undefined) => Number(value ?? 0);
const pct = (value: number) => `${(value * 100).toFixed(1)}%`;

/**
 * Menu engineering (REPORTS.md C6): each item's popularity (share of the items sold against 70% of an equal share) and its unit
 * margin from the standard cost (against the weighted average), classed as stars, plowhorses, puzzles and dogs.
 */
export function MenuEngineeringPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const [range, setRange] = useReportRange();
  const r = (key: string, options?: Record<string, unknown>) => t(`reports.menu.${key}` as I18nKey, options);
  const menu = useQuery({
    queryKey: ['reports', 'menu', range],
    queryFn: () =>
      unwrap<Menu>(hoApi.GET('/api/v1/reporting/sales/menu-engineering', { params: { query: rangeQuery(range) } })),
  });
  const items = menu.data?.items ?? [];
  const count = (c: string) => items.filter((i) => i.class === c).length;

  return (
    <ReportFrame
      title={r('title')}
      description={r('description')}
      range={range}
      onRange={setRange}
      error={menu.error}
      csvName="menu-engineering"
      csv={
        items.length > 0
          ? () => [
              [
                r('item'),
                r('category'),
                t('reports.quantity'),
                t('reports.net'),
                r('cost'),
                r('mix'),
                r('margin'),
                r('class'),
              ],
              ...items.map((i) => [
                i.name,
                i.categoryName ?? '',
                n(i.quantity),
                n(i.netAmount),
                i.costAmount == null ? '' : n(i.costAmount),
                n(i.mixShare),
                i.unitMargin == null ? '' : n(i.unitMargin),
                r(`classes.${i.class}`),
              ]),
            ]
          : undefined
      }
    >
      <StatTiles>
        {CLASSES.map((c) => (
          <StatTile
            key={c}
            label={r(`classes.${c}`)}
            value={format.number(count(c))}
            caption={r(`hints.${c}`)}
            isLoading={menu.isLoading}
          />
        ))}
      </StatTiles>
      <ListTable<Item & { id: string }>
        title={r('items')}
        description={
          menu.data && items.length > 0
            ? `${r('thresholds', {
                popular: pct(n(menu.data.popularityThreshold)),
                margin: menu.data.averageMargin == null ? '—' : format.number(n(menu.data.averageMargin)),
              })} ${r('note')}`
            : r('note')
        }
        columns={[
          { key: 'name', header: r('item') },
          { key: 'categoryName', header: r('category'), render: (i) => i.categoryName ?? '' },
          {
            key: 'quantity',
            header: t('reports.quantity'),
            numeric: true,
            render: (i) => format.number(n(i.quantity)),
          },
          { key: 'mixShare', header: r('mix'), numeric: true, render: (i) => pct(n(i.mixShare)) },
          { key: 'netAmount', header: t('reports.net'), numeric: true, render: (i) => format.number(n(i.netAmount)) },
          {
            key: 'unitMargin',
            header: r('margin'),
            numeric: true,
            render: (i) => (i.unitMargin == null ? '' : format.number(n(i.unitMargin))),
          },
          {
            key: 'class',
            header: r('class'),
            render: (i) => <Tag type={CLASS_TAG[i.class] ?? 'gray'}>{r(`classes.${i.class}`)}</Tag>,
          },
        ]}
        rows={items.map((i) => ({ ...i, id: i.productId }))}
        ariaLabel={r('title')}
        loading={menu.isLoading}
        empty={{ heading: t('reports.noSales') }}
      />
    </ReportFrame>
  );
}
